import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { cn } from '@/lib/tailwind.utils'
import { appLink } from '@app/config/appLink'
import { ChevronRight, Menu } from 'lucide-react'
import { USE_CASES } from '../config/useCases.config'
import { accentBgMap } from '../types'
import { NavbarSkeleton } from './NavbarSkeleton'
import { getNavLinkClassName } from '../utils/navItems.utils'
import { useHomeNavigation } from '../hooks/useHomeNavigation'

interface HomeV2NavbarMobileProps {
  className?: string
}

const HomeV2NavbarMobile = ({ className }: HomeV2NavbarMobileProps) => {
  const { isLoading, navigate, pathname, logos, activeStates, showData, showDataUpload } =
    useHomeNavigation()

  const { isWelcomeActive, isFeaturesActive, isDataActive, isDataUploadActive } = activeStates

  const getItemClassName = (isActive = false) =>
    cn(
      'flex items-center justify-between border-b px-6 py-4 text-sm font-medium transition-colors border-border',
      getNavLinkClassName(isActive),
    )

  const iconClassName = 'h-4 w-4 text-muted-foreground'

  if (isLoading) return <NavbarSkeleton />

  return (
    <div
      className={cn(
        'flex h-14 w-full items-center justify-between border-b px-6 border-border bg-white',
        className,
      )}
    >
      <img src={logos.full} alt="Logo" className="h-8 w-auto" />

      <Sheet>
        <SheetTrigger className="flex items-center justify-center rounded-md p-2 transition-colors hover:bg-accent">
          <Menu className="h-5 w-5 text-foreground" />
        </SheetTrigger>

        <SheetContent side="top" className="bg-white px-0 pt-14">
          <nav className="flex flex-col">
            <button
              type="button"
              onClick={() => navigate(appLink.homeWelcome.path)}
              className={getItemClassName(isWelcomeActive)}
            >
              {appLink.homeWelcome.label}
              <ChevronRight className={iconClassName} />
            </button>

            <button
              type="button"
              onClick={() => navigate(appLink.homeFeatures.path)}
              className={getItemClassName(isFeaturesActive)}
            >
              {appLink.homeFeatures.label}
              <ChevronRight className={iconClassName} />
            </button>

            <div className="border-b border-border px-6 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {appLink.homeUseCases.label}
            </div>

            {USE_CASES.map((uc) => {
              const Icon = uc.iconComponent
              const isActive = pathname.includes(`/use-cases/${uc.id}`)

              return (
                <button
                  key={uc.id}
                  type="button"
                  onClick={() => navigate(`${appLink.homeUseCases.path}/${uc.id}/overview`)}
                  className={cn(
                    'flex items-center gap-3 border-b px-6 py-4 text-sm font-medium transition-colors border-border',
                    getNavLinkClassName(isActive),
                  )}
                >
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                      accentBgMap[uc.accentColor],
                    )}
                  >
                    <Icon />
                  </div>
                  <span className="flex-1 text-left">{uc.title}</span>
                  <ChevronRight className={iconClassName} />
                </button>
              )
            })}

            {showData && (
              <button
                type="button"
                onClick={() => navigate(appLink.homeData.path)}
                className={getItemClassName(isDataActive)}
              >
                {appLink.homeData.label}
                <ChevronRight className={iconClassName} />
              </button>
            )}

            {showDataUpload && (
              <button
                type="button"
                onClick={() => navigate(appLink.homeDataUpload.path)}
                className={getItemClassName(isDataUploadActive)}
              >
                {appLink.homeDataUpload.label}
                <ChevronRight className={iconClassName} />
              </button>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default HomeV2NavbarMobile
